import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Store } from '@ngrx/store';
import { ResetState, SetNewJWTToken } from '../store/authentication.actions';

@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {
  private readonly store = inject(Store);
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(private http: HttpClient) {}

  intercept(req: HttpRequest<never>, next: HttpHandler): Observable<HttpEvent<string>> {
    let newRequest = this.getRequestWithoutAnonymousHeader(req);
    newRequest = this.getRequestWithToken(newRequest, sessionStorage.getItem(environment.authTokenName));

    return next.handle(newRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && !newRequest.url.includes('/refresh-token')) {
          return this.handle401Error(newRequest, next);
        }
        return throwError(() => error);
      })
    );
  }

  private handle401Error(req: HttpRequest<never>, next: HttpHandler): Observable<HttpEvent<string>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = sessionStorage.getItem('refresh-token');
      if (!refreshToken) {
        this.isRefreshing = false;
        return throwError(() => new Error('Refresh token not available'));
      }

      return this.http.post<{ token: string }>(`${environment.serverUrl}/auth/refresh-token`, { refreshToken }).pipe(
        switchMap((response) => {
          const newToken = response.token;
          if (newToken) {
            sessionStorage.setItem(environment.authTokenName, newToken);
            this.store.dispatch(SetNewJWTToken({ token: newToken }));
            this.refreshTokenSubject.next(newToken);
          }
          this.isRefreshing = false;
          return next.handle(this.getRequestWithToken(req, newToken));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.store.dispatch(ResetState());
          this.refreshTokenSubject.next(null);
          return throwError(() => err);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => next.handle(this.getRequestWithToken(req, token)))
      );
    }
  }

  private getRequestWithoutAnonymousHeader(req: HttpRequest<never>) {
    if (!req.headers.has('Anonymous')) return req;
    return req.clone({ headers: req.headers.delete('Anonymous') });
  }

  private getRequestWithToken(req: HttpRequest<never>, token: string | null) {
    if (!token) return req;
    return req.clone({ setHeaders: { Accept: 'application/json', Authorization: `JWT ${token}` } });
  }
}
